import {compact, last, noop} from 'lodash';
import {DateTime} from 'luxon';
import React from 'react';

import {isElectron} from '../../../libs/shared/core/src/helpers/environment/platformHelpers';
import {Trans} from '../../../libs/shared/core/src/helpers/internationalization/react';
import {MessageTypesEnum} from '../../../libs/shared/core/src/models/api/commonMessageModel';
import {getIsUserGlobalAdmin} from '../../../libs/shared/core/src/store/data/access/commonAccessSelectors';
import {ViewerPanelId} from '../../../libs/shared/core/src/store/panels/panelStateBase';
import {openInBrowser} from '../../../srcCommon/browser/openInBrowser';
import {useAppDispatch, useAppSelector} from '../../helpers/appReduxHelpers';
import {OnboardingTipTypesEnum, StartGuideTaskTypesEnum} from '../../models/api/preferencesModel';
import {
  SettingsInboxesResourcePagesEnum,
  SettingsInboxesTabPagesEnum,
} from '../../models/url/models/settingsInboxesUrlModel';
import {SettingsRootPagesEnum} from '../../models/url/models/types';
import {buildSettingsPath} from '../../models/url/settingsUrlHelpers';
import {SettingsAdminHomeUrl} from '../../models/url/settingsUrlModel';
import {useHomeShortcutClick} from '../../settings/home/settingsHomeShortcutHook';
import {SettingsHomeDestinationsEnum} from '../../settings/home/settingsHomeTypes';
import {makeGetComposeChannelExcludingDemo} from '../../store/data/channels/channelsSelectors';
import {getCompany, getIsCustomer} from '../../store/data/company/companySelectors';
import {composeMessageAction} from '../../store/data/messages/draftMessagesActions';
import {updateStartGuideTasksAction} from '../../store/data/preferences/preferencesActions';
import {getCompletedStartGuideTasks} from '../../store/data/preferences/preferencesSelectors';
import {getIsUserFirstGlobalAdmin, getUser} from '../../store/data/teammates/teammatesSelectors';
import {getLastUpdatedTeam} from '../../store/data/teams/teamsSelectors';
import {navigateToLinkAction} from '../../store/stateless/navigationActions';
import {getShouldAddIndividualInbox} from '../../store/stateless/startGuideSelectors';
import {useTaskCompletionProgressWithExploreRulesV0} from '../onboarding/admin/adminOnboardingHelpers';
import {
  inboxTaskDescription,
  inboxTaskTitle,
  teammatesTaskDescription,
  teammatesTaskTitle,
} from '../onboarding/admin/tasks/adminOnboardingText';
import {StartGuideTaskDefinition, StartGuideTaskInteractionsEnum} from './startGuideCommon';

interface StartGuideHelperArgs {
  panelId: ViewerPanelId;
  appPath: string;
  composeTeamInboxAccessRequest: () => void;
}

// A hook that computes and returns values used by the start guide.
export function useStartGuideHelper({panelId, appPath, composeTeamInboxAccessRequest}: StartGuideHelperArgs) {
  const dispatch = useAppDispatch();
  const isUserFirstGlobalAdmin = useAppSelector(getIsUserFirstGlobalAdmin);
  const isUserGlobalAdmin = useAppSelector(getIsUserGlobalAdmin);
  const completedStartGuideTasks = useAppSelector(getCompletedStartGuideTasks);

  const newCompanyCutoffTime = DateTime.local().minus({months: 6}).toMillis();
  const company = useAppSelector(getCompany);
  const isNewCompany = company.createdAt >= newCompanyCutoffTime;

  // Create account task. This is a "dummy" task to give the user a sense of progress/momentum.
  let createAccountTask: StartGuideTaskDefinition | undefined;
  if (!isUserFirstGlobalAdmin) {
    createAccountTask = {
      title: <Trans id="startguide-task-title-createaccount">Complete Front tour</Trans>,
      description: <Trans id="startguide-task-description-createaccount">{'< 3 min'}</Trans>,
      interactionId: StartGuideTaskInteractionsEnum.TOUR_INTRO,
      iconName: 'sparklesSmall',
      isIncomplete: false,
      onClick: noop,
    };
  }

  // Default to adding inboxes and answers to the last updated team, which is likely the only one anyway.
  const lastUpdatedTeam = useAppSelector(getLastUpdatedTeam);
  const targetNamespace = lastUpdatedTeam && lastUpdatedTeam.namespace;

  const {shouldAddTeamInbox, shouldAddTeammate, shouldExploreRules, numCompletedTasks, numTotalTasks} =
    useTaskCompletionProgressWithExploreRulesV0();

  /**
   * Add team inbox task.
   *
   * Trigger: User is a global admin in trial.
   * Action: Send to team inbox page in settings.
   * Completion: Access to a team inbox.
   */
  let addTeamInboxTask: StartGuideTaskDefinition | undefined;
  if (targetNamespace) {
    addTeamInboxTask = {
      title: inboxTaskTitle,
      description: inboxTaskDescription,
      interactionId: StartGuideTaskInteractionsEnum.ADD_TEAM_INBOX,
      iconName: 'inboxSmall',
      isIncomplete: shouldAddTeamInbox,
      renderTooltip: undefined,
      onClick: () =>
        dispatch(
          navigateToLinkAction(
            buildSettingsPath({
              namespace: targetNamespace,
              rootPage: SettingsRootPagesEnum.INBOXES,
              resourcePage: SettingsInboxesResourcePagesEnum.NEW,
              tabPage: SettingsInboxesTabPagesEnum.INBOXES,
            }),
          ),
        ),
    };
  }

  const url: SettingsAdminHomeUrl = {rootPage: SettingsRootPagesEnum.ADMIN_HOME};
  const {onShortcutClick} = useHomeShortcutClick(url);

  /**
   * Add teammate task.
   *
   * Trigger: User is a global admin in trial.
   * Action: Send to teammate invite page in settings.
   * Completion: Teammate added to company.
   */
  const addTeammateTask: StartGuideTaskDefinition = {
    title: teammatesTaskTitle,
    description: teammatesTaskDescription,
    interactionId: StartGuideTaskInteractionsEnum.INVITE_TEAM,
    iconName: 'teammatesSmall',
    isIncomplete: shouldAddTeammate,
    renderTooltip: undefined,
    onClick: () => onShortcutClick(SettingsHomeDestinationsEnum.ADD_TEAMMATES),
  };

  /**
   * Download app task.
   *
   * Trigger: All startGuide users get this task.
   * Action: Send user to download page on website and complete the download
   * app startGuide task.
   * Completion: Click this task or use the desktop app.
   */
  const downloadAppTask: StartGuideTaskDefinition = {
    title: <Trans id="startguide-task-title-download">Download desktop app</Trans>,
    description: <Trans id="startguide-task-description-download">{'< 30 sec'}</Trans>,
    interactionId: StartGuideTaskInteractionsEnum.DOWNLOAD_APP,
    iconName: 'downloadSmall',
    isIncomplete: !isElectron() && !completedStartGuideTasks.includes(StartGuideTaskTypesEnum.DOWNLOAD_APP),
    renderTooltip: () => <Trans id="startguide-task-tooltip-download">Work in Front from your desktop</Trans>,
    onClick: () => {
      dispatch(updateStartGuideTasksAction(StartGuideTaskTypesEnum.DOWNLOAD_APP));
      openInBrowser('https://frontapp.com/download');
    },
  };

  const shouldAddIndividualInbox = useAppSelector(getShouldAddIndividualInbox);
  /**
   * Add individual inbox task.
   *
   * Trigger: All startGuide users get this task.
   * Action: Send to user inbox page in settings.
   * Completion: Add an individual inbox.
   */
  const addIndividualInboxTask: StartGuideTaskDefinition = {
    title: <Trans id="startguide-task-title-individualinbox">Add your work email</Trans>,
    description: <Trans id="startguide-task-description-individualinbox">{'< 1 min'}</Trans>,
    interactionId: StartGuideTaskInteractionsEnum.ADD_WORK_EMAIL,
    iconName: 'messageSmall',
    isIncomplete: shouldAddIndividualInbox,
    renderTooltip: () => (
      <Trans id="startguide-task-tooltip-individualinbox">Consolidate all your work in one place</Trans>
    ),
    onClick: () => noop,
  };

  /**
   * Attend live training session task.
   *
   * Trigger: Non-admin teammate.
   * Action: Open link to webinar registration.
   * Completion: Click the link.
   */
  let attendLiveTrainingTask: StartGuideTaskDefinition | undefined;
  const shouldShowAttendLiveTrainingTask = useAppSelector(getIsCustomer);
  if (shouldShowAttendLiveTrainingTask) {
    attendLiveTrainingTask = {
      title: <Trans id="startguide-task-title-attendlivetraining">Attend a live training</Trans>,
      description: <Trans id="startguide-task-description-attendlivetraining">{'< 5 min'}</Trans>,
      interactionId: StartGuideTaskInteractionsEnum.ATTEND_LIVE_TRAINING,
      iconName: 'calendarInvite',
      isIncomplete: !completedStartGuideTasks.includes(StartGuideTaskTypesEnum.ATTEND_LIVE_TRAINING),
      onClick: () => {
        openInBrowser(
          'https://frontapp.com/resources/webinar-teach-me-how-to-front-or-live-training?utm_source=frontapp&utm_campaign=SCOD22_MCOD7_CRCO15&utm_medium=in_app&utm_content=p4_start_guide',
        );
        dispatch(updateStartGuideTasksAction(StartGuideTaskTypesEnum.ATTEND_LIVE_TRAINING));
      },
    };
  }

  /**
   * Request access to team inbox task.
   *
   * Trigger: User is an invited teammate.
   * Action: Open a draft discussion to request access to a team inbox.
   * Completion: Teammate has access to a team inbox.
   */
  const requestTeamInboxTask: StartGuideTaskDefinition = {
    title: <Trans id="startguide-task-title-requestteaminbox">Access your shared inbox</Trans>,
    description: <Trans id="startguide-task-description-requestteaminbox">{'< 1 min'}</Trans>,
    interactionId: StartGuideTaskInteractionsEnum.ACCESS_TEAM_INBOX,
    iconName: 'inboxSmall',
    isIncomplete: shouldAddTeamInbox,
    onClick: () => dispatch(composeTeamInboxAccessRequest),
  };

  const emailComposeChannel = useAppSelector(
    makeGetComposeChannelExcludingDemo<void>({
      getSpecifiedMessageType: () => MessageTypesEnum.EMAIL,
    }),
  );

  /**
   * Send email task.
   *
   * Trigger: User is an invited teammate.
   * Action: Open an email composer.
   * Completion: Teammate has sent an email.
   */
  const sendEmailTask: StartGuideTaskDefinition = {
    title: <Trans id="startguide-task-title-sendemail">Send your first email</Trans>,
    description: <Trans id="startguide-task-description-sendemail">{'< 1 min'}</Trans>,
    interactionId: StartGuideTaskInteractionsEnum.SEND_FIRST_EMAIL,
    iconName: 'sendSmall',
    isIncomplete: !completedStartGuideTasks.includes(StartGuideTaskTypesEnum.SEND_EMAIL),
    isDisabled: !emailComposeChannel,
    onClick: () => {
      if (!emailComposeChannel) {
        return;
      }

      dispatch(composeMessageAction({channelId: emailComposeChannel.id}));
    },
  };

  // Get location to determine if we are viewing a conversation. Assume we
  // are in a conversation if last part of the path is greater than 0.
  // TODO: Use the viewer panel state to determine this.
  const lastPathElement = last(appPath.split('/'));
  const isConversation = Boolean(lastPathElement && parseInt(lastPathElement, 10) > 0);

  /**
   * Mention teammate in comment task.
   *
   * Trigger: User is an invited teammate.
   * Action: Show infobubble around comment bar when a conversation is
   * visible in P3.
   * Completion: Teammate has mentioned a teammate.
   */
  const mentionTeammateTask: StartGuideTaskDefinition = {
    title: <Trans id="startguide-task-title-mentionteammate">@mention a teammate</Trans>,
    description: <Trans id="startguide-task-description-mentionteammate">{'< 1 min'}</Trans>,
    interactionId: StartGuideTaskInteractionsEnum.MENTION_TEAMMATE,
    iconName: 'mentionSmall',
    isIncomplete: !completedStartGuideTasks.includes(StartGuideTaskTypesEnum.MENTION_TEAMMATE),
    isDisabled: !isConversation,
    renderTooltip: () =>
      isConversation ? null : (
        <Trans id="startguide-task-tooltip-mentionteammate">Please open a message first</Trans>
      ),
    infoBubble: OnboardingTipTypesEnum.START_GUIDE_COMMENT_COMPOSER,
    onClick: noop,
  };

  /**
   * Explore company settings task.
   *
   * Action: Navigate to team rules or settings homepage (if available).
   */
  let exploreSettingsTask: StartGuideTaskDefinition | undefined;
  if (targetNamespace) {
    exploreSettingsTask = {
      title: <Trans id="startguide-task-title-exploresettings">Explore settings</Trans>,
      description: <Trans id="startguide-task-description-exploresettings">{'< 3 min'}</Trans>,
      interactionId: StartGuideTaskInteractionsEnum.EXPLORE_SETTINGS,
      iconName: 'gearSmall',
      isIncomplete: shouldExploreRules,
      renderTooltip: () => (
        <Trans id="startguide-task-tooltip-exploresettings">
          Setup your workflow with rules, tags, and responses in settings
        </Trans>
      ),
      onClick: () => {
        dispatch(navigateToLinkAction('/settings/home'));

        dispatch(updateStartGuideTasksAction(StartGuideTaskTypesEnum.ADD_TEAM_RULES));
      },
    };
  }

  // There are three task lists. One for nonglobal admins, one for global admins, and
  // one for the onboarding experiment global admins.
  const nonGlobalAdminTasks = compact([
    createAccountTask,
    requestTeamInboxTask,
    mentionTeammateTask,
    addIndividualInboxTask,
    sendEmailTask,
    downloadAppTask,
    attendLiveTrainingTask,
  ]);

  // Companies 6 months or younger should see the 1-on-1 training task plus
  // other actions.
  // Otherwise, they should only see the live training task.
  const globalAdminTasks = isNewCompany
    ? compact([
        createAccountTask,
        addTeammateTask,
        addTeamInboxTask,
        exploreSettingsTask,
        addIndividualInboxTask,
        downloadAppTask,
      ])
    : compact([attendLiveTrainingTask]);

  const computeHasCompletedApplicableTasks = () => {
    if (isUserGlobalAdmin) {
      return globalAdminTasks.every((task) => !task.isIncomplete);
    }
    return nonGlobalAdminTasks.every((task) => !task.isIncomplete);
  };

  const computeTasks: () => ReadonlyArray<StartGuideTaskDefinition> = () => {
    if (isUserGlobalAdmin) {
      return globalAdminTasks;
    }
    return nonGlobalAdminTasks;
  };

  // Only show tasks if user account is under two weeks or doesn't have the feature flag
  const newUserTaskCuttoffTime = DateTime.local().minus({weeks: 4}).toMillis();
  const user = useAppSelector(getUser);
  const isNewUser = user.createdAt >= newUserTaskCuttoffTime;

  const hasCompletedTasks = computeHasCompletedApplicableTasks();
  const shouldShowStartGuideTasks =
    // Show start guide in P4 only if we're not a global admin
    !isUserGlobalAdmin &&
    isNewUser &&
    !hasCompletedTasks &&
    !completedStartGuideTasks.includes(StartGuideTaskTypesEnum.HELP_CENTER_SUCCESS_DISMISS);

  // Success state for tasks
  const shouldShowSuccessScreen =
    isNewUser &&
    hasCompletedTasks &&
    !completedStartGuideTasks.includes(StartGuideTaskTypesEnum.HELP_CENTER_SUCCESS_DISMISS);

  return {
    isUserFirstGlobalAdmin,
    shouldShowSuccessScreen,
    shouldShowStartGuideTasks,
    tasks: computeTasks(),
    numCompletedTasks,
    numTotalTasks,
  };
}
